import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';

import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import { HelpWidgetService } from 'src/app/app-common/services/help-widget.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { CompanyIcpService } from 'src/app/components/company-icp/company-icp.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalStackService } from 'src/app/components/modals/modal-stack.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { UpgradeNoticeComponent } from 'src/app/components/plans/upgrade-notice/upgrade-notice.component';
import { ValidationService } from 'src/app/shared/services/validation.service';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { CompanyAccessService } from '../../services/company-access.service';

@Component({
  selector: 'user-fields',
  templateUrl: './user-fields.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  styleUrls: ['./user-fields.component.scss'],
  standalone: false,
})
export class UserFieldsComponent implements OnInit, AfterViewInit {
  USER_ROLES = [
    { key: 'ap', value: 'Teacher', checked: false },
    { key: 'sm', value: 'Screen Share Moderator', checked: false },
    { key: 'ce', value: 'Content Editor', checked: false },
    { key: 'cp', value: 'Content Publisher', checked: false },
    { key: 'da', value: 'Display Administrator', checked: false },
    { key: 'ua', value: 'System Administrator', checked: false },
    { key: 'ba', value: 'Billing Administrator', checked: false },
    {
      key: 'sa',
      value: 'Rise System Administrator',
      checked: false,
      admin: true,
    },
  ];

  private _user: any;
  @Input() set user(value: any) {
    this._user = value;
    this.editUser = { ...value }; // Make a shallow copy for editing
    this.updateRoles();
    this.initializeUserDependentData();
  }

  @Input() isAddMode = false;

  @Input() actions: any;

  @ViewChild('activationLinkText')
  private activationLinkText: ElementRef;

  @ViewChildren('UpgradeNotice') notices: QueryList<UpgradeNoticeComponent>;

  get user(): any {
    return this._user;
  }

  public userForm: NgForm;
  public editUser: any = { settings: {} };

  COMPANY_ROLE_FIELDS;

  EMAIL_REGEX = ValidationService.EMAIL_REGEX;

  userPassword: any = {};
  editingYourself: boolean = false;
  activationLink: string;
  isRiseAuthUser = this.userStateService.isRiseAuthUser();
  showChangePassword = false;
  formError = null;
  apiError = null;
  showActivationLink = false;

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private controlContainer: ControlContainer,
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    protected companyAccessService: CompanyAccessService,
    private trackerService: TrackerService,
    private featuresService: FeaturesService,
    public helpWidgetService: HelpWidgetService,
    private changeDetectorRef: ChangeDetectorRef,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private clipboardService: ClipboardService,
  ) {
    this.processErrorCode = processErrorCodeAdapter.createCallable();
  }

  ngOnInit() {
    this.userForm = <NgForm>this.controlContainer;

    if (
      this.companyStateService.isEducationCustomer(
        this.userStateService.checkUsername(this.user.username),
      )
    ) {
      this.COMPANY_ROLE_FIELDS =
        CompanyIcpService.EDUCATION_COMPANY_ROLE_FIELDS;
    } else {
      this.COMPANY_ROLE_FIELDS = CompanyIcpService.COMPANY_ROLE_FIELDS;
    }
  }

  ngAfterViewInit() {
    UpgradeNoticeComponent.hidePopovers(this.notices);
  }

  get teacherRoleAvailable(): boolean | null {
    return this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_TEACHER_ROLE,
    );
  }

  get screenSharingAvailable(): boolean | null {
    return this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_SCREEN_SHARING,
    );
  }

  get showChangePasswordAction(): boolean {
    return (
      this.editingYourself &&
      !this.isAddMode &&
      !this.user?.ssouserProvisioningEnabled
    );
  }

  get showDeleteMenuDivider(): boolean {
    const hasOtherMoreOptions = (this.actions?.moreOptions || []).some(
      (action) => !action.disabled && action.id !== 'changePasswordButton',
    );

    return this.showChangePasswordAction || hasOtherMoreOptions;
  }

  get showPasswordForm(): boolean {
    return (
      this.showChangePassword && this.editingYourself && this.isRiseAuthUser
    );
  }

  updateRoles() {
    for (let role of this.USER_ROLES) {
      role.checked = this.isRoleChecked(role);
    }
  }

  isRoleChecked(role) {
    return this.user.roles && this.user.roles.indexOf(role.key) > -1;
  }

  onCheckboxChange(role) {
    if (!this.editUser.roles) {
      this.editUser.roles = [];
    }

    const index = this.editUser.roles.indexOf(role.key);

    if (role.key === 'ap') {
      // If selecting teacher role, clear all other roles except screen share moderator
      const smRole = this.editUser.roles.includes('sm') ? ['sm'] : [];
      this.editUser.roles = index === -1 ? [...smRole, role.key] : smRole;
    } else if (role.key === 'sm') {
      // Screen share moderator can be selected independently
      if (index > -1) {
        this.editUser.roles = this.editUser.roles.filter((r) => r !== role.key);
      } else {
        this.editUser.roles = [...this.editUser.roles, role.key];
      }
    } else {
      // For all other roles, remove teacher role if present
      const rolesWithoutTeacher = this.editUser.roles.filter((r) => r !== 'ap');

      // Toggle the selected role
      if (index > -1) {
        this.editUser.roles = rolesWithoutTeacher.filter((r) => r !== role.key);
      } else {
        this.editUser.roles = [...rolesWithoutTeacher, role.key];
      }
    }

    this.trackerService.userEvent(
      'User Roles Changed',
      this.userStateService.getUsername(),
      this.userStateService.checkUsername(this.user.username),
      {
        updatedUserId: this.user.username,
        roles: this.user.roles.join(', '),
      },
    );

    this.updateRoles();
  }

  editRoleAllowed(role) {
    if (role.key === 'ap') {
      const isUserNotSelfAndFeatureIsAvailable =
        !this.userStateService.checkUsername(this.user.username) &&
        this.teacherRoleAvailable;

      return isUserNotSelfAndFeatureIsAvailable;
    } else if (role.key === 'sm') {
      return this.screenSharingAvailable;
    }

    if (this.userStateService.isRiseAdmin()) {
      return true;
    } else if (this.userStateService.isUserAdmin()) {
      if (role.key === 'sa') {
        return false;
      } else if (
        role.key === 'ua' &&
        this.userStateService.checkUsername(this.user.username)
      ) {
        //cannot unassign oneself from ua
        return false;
      } else {
        return true;
      }
    } else {
      //do not allow user to check/uncheck role by default
      return false;
    }
  }

  editRoleVisible(role) {
    if (role.key === 'ap') {
      return (
        this.companyStateService.isK12Customer() ||
        this.companyStateService.isHigherEdCustomer()
      );
    } else if (this.userStateService.isRiseAdmin()) {
      if (
        this.companyStateService.isSubcompanySelected() &&
        role.key === 'sa'
      ) {
        return false;
      } else {
        return true;
      }
    } else if (
      this.userStateService.isUserAdmin() ||
      this.userStateService.isRiseVisionUser()
    ) {
      if (role.key === 'sa') {
        return false;
      } else {
        return true;
      }
    } else {
      // in practice should never reach here
      return false;
    }
  }

  closeAllModals() {
    ModalStackService.closeAll();
  }

  showUpgradePlanModal() {
    this.featuresService
      .showUpgradePlanModal(FeaturesService.FEATURE_TEACHER_ROLE)
      .then((result) => {
        if (result) {
          this.closeAllModals();
        }
      });
  }

  private initializeUserDependentData() {
    if (this._user) {
      this.editingYourself = this.userStateService.checkUsername(
        this._user.username,
      );
    }
  }

  createActivationLink() {
    const token = new TextEncoder().encode(
      this.user.id + new Date(this.user.creationDate).getTime(),
    );

    crypto.subtle.digest('SHA-1', token).then((digest) => {
      const url =
        window.document.location.protocol +
        '//' +
        window.document.location.host +
        '/joinaccount';
      const companyName = encodeURIComponent(
        this.companyStateService.getCopyOfSelectedCompany().name,
      );
      const userId = encodeURIComponent(this.user.id);
      const hex = [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');

      this.activationLink = `${url}/${companyName}/${userId}/${hex}`;
      this.changeDetectorRef.detectChanges();
      this.activationLinkText.nativeElement.select();
    });
  }

  _showErrorMessage(action, error) {
    this.formError = 'Failed to ' + action + ' User.';
    this.apiError = this.processErrorCode(error);
  }

  toggleChangePassword() {
    this.showChangePassword = !this.showChangePassword;

    if (this.showChangePassword) {
      this._setPasswordError(null);
    }
  }

  _setPasswordError(error: any) {
    if (this.userForm.controls['currentPassword']) {
      this.userForm.controls.currentPassword.setErrors(error);
    }
  }

  currentPasswordUpdated() {
    if (
      this.userForm.controls['currentPassword'] &&
      this.userForm.controls.currentPassword.errors &&
      this.userForm.controls.currentPassword.errors.currentPasswordNotValid
    ) {
      this._setPasswordError(null);
    }
  }

  toggleActivationLinkVisibility() {
    this.showActivationLink = !this.showActivationLink;
    if (this.showActivationLink && !this.activationLink) {
      this.createActivationLink();
    }
  }

  copyActivationLink(link: string): Promise<void> {
    return this.clipboardService.copy(link);
  }
}
